// The client's side of the live wait (lib/live-vantan.ts; genomgang-spel-latens-plan.md §6.5 and §8.4.7), shared by
// the activities' state hooks. One instance per hook. It decides three things for the poll chain (lib/pollkedja.ts):
//
//   - whether a poll asks the server to wait (`w=1`): not while switched off after trouble, and for a student only
//     in a visible tab (a hidden one keeps its slow interval);
//   - the pause before the next poll: after a wait that ran its course or ended on a change, a short spread pause
//     (LIVE_PAUS_*); otherwise null = the hook's own interval, exactly as before the live wait;
//   - when to stop asking for LIVE_AV_EFTER_FEL_MS: 3 failed waits in a row (network, the client's own timeout,
//     5xx), or the floor — more than 6 waiting polls in 10 s that brought nothing new (a server answering
//     "waited" at once, or a view the client already had) — so a fault can never turn the wait into a tight loop.
//     A kick that aborts a wait (a teacher's step, a POST) is not a failure.
//
// The client's own timeout uses a plain controller and setTimeout: AbortSignal.any/AbortSignal.timeout are missing
// on the older iPads in the classrooms.

import { LIVE_ANROP_TIMEOUT_MS, LIVE_AV_EFTER_FEL_MS, LIVE_PAUS_MIN_MS, LIVE_PAUS_SPRIDNING_MS } from "./klient-intervall";

/** A wait that answers "waited" sooner than this did not really wait. */
const KORT_VANTAN_MS = 2_000;
const GOLV_FONSTER_MS = 10_000;
const GOLV_MAX = 6;
const FEL_I_RAD = 3;

export interface LiveUtfall {
  /** The server said it waited ({ vantade: true }). */
  vantade: boolean;
  /** The answer brought something the client did not have (a new version or puls key). */
  nytt: boolean;
  /** How long the request took. */
  ms: number;
}

export interface LiveKlient {
  /** A poll starts: should it ask the server to wait? `synlig`: the tab is visible (ignored for the teacher's
   *  views). Forgets the last poll's pause, so only an answer to THIS poll can earn the short one. */
  borja(synlig: boolean): boolean;
  /** The chain's signal plus the client's own timeout; call `stang` when the request is over. */
  medTimeout(signal: AbortSignal): { signal: AbortSignal; stang(): void; tidUte(): boolean };
  /** A waiting poll answered 2xx. */
  svar(u: LiveUtfall): void;
  /** A waiting poll failed (not a kick's abort). */
  fel(): void;
  /** The pause before the next poll, or null for the hook's own interval. */
  paus(): number | null;
}

export interface LiveKlientBeroenden {
  nu?: () => number;
  slump?: () => number;
  setTimeout?: (fn: () => void, ms: number) => unknown;
  clearTimeout?: (id: unknown) => void;
}

export function skapaLiveKlient(arLarare: boolean, d: LiveKlientBeroenden = {}): LiveKlient {
  const nu = d.nu ?? Date.now;
  const slump = d.slump ?? Math.random;
  const satt = d.setTimeout ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const rensa = d.clearTimeout ?? ((id: unknown) => clearTimeout(id as ReturnType<typeof setTimeout>));
  let avTill = 0;
  let felIRad = 0;
  let kortPaus = false;
  const tomma: number[] = [];

  const stangAv = () => {
    avTill = nu() + LIVE_AV_EFTER_FEL_MS;
    felIRad = 0;
    tomma.length = 0;
  };

  return {
    borja(synlig) {
      kortPaus = false;
      return nu() >= avTill && (arLarare || synlig);
    },
    medTimeout(signal) {
      const ctrl = new AbortController();
      let ute = false;
      const vidbefordra = () => ctrl.abort();
      if (signal.aborted) ctrl.abort();
      else signal.addEventListener("abort", vidbefordra, { once: true });
      const timer = satt(() => {
        ute = true;
        ctrl.abort();
      }, LIVE_ANROP_TIMEOUT_MS);
      return {
        signal: ctrl.signal,
        stang() {
          rensa(timer);
          signal.removeEventListener("abort", vidbefordra);
        },
        tidUte: () => ute,
      };
    },
    svar(u) {
      felIRad = 0;
      const tom = !u.nytt && (!u.vantade || u.ms < KORT_VANTAN_MS);
      kortPaus = !tom && (u.vantade || u.nytt);
      if (tom) {
        const t = nu();
        tomma.push(t);
        while (tomma.length && t - tomma[0] > GOLV_FONSTER_MS) tomma.shift();
        if (tomma.length > GOLV_MAX) stangAv();
      }
    },
    fel() {
      kortPaus = false;
      if (++felIRad >= FEL_I_RAD) stangAv();
    },
    paus() {
      return kortPaus && nu() >= avTill ? LIVE_PAUS_MIN_MS + Math.floor(slump() * LIVE_PAUS_SPRIDNING_MS) : null;
    },
  };
}
